<template>
    <div class="h-full relative" ref="inputElement">
      <Combobox :model-value="props.modelValue" :multiple="!singleValue" :nullable="nullable" @update:modelValue="value => emit('update:modelValue', value)">
        <div class="relative h-full">
          <div
            class="relative h-full w-full cursor-default text-left text-xs shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300"
            :class="[roundedClass, inputClass || 'bg-white']"
          >
            <ComboboxInput 
              class="w-full border-none h-full py-2 pl-3 text-xs leading-5 text-gray-900 focus:ring-0"
              :class="[roundedClass, showClear ? 'pr-14' : 'pr-10']" :placeholder="placeholder" @mouseover="showPopover"  @mouseleave="hidePopover" 
              @change="query = $event.target.value"
              :display-value="display"
            />
            <button
              v-if="showClear"
              type="button"
              class="absolute inset-y-0 right-7 flex items-center px-1"
              @click="emit('update:modelValue', singleValue ? null : [])"
            >
              <XMarkIcon class="h-4 w-4 text-gray-400" aria-hidden="true" />
            </button>
            <ComboboxButton
              class="absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <TransitionRoot
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            @after-leave="query = ''; visibleCount = pageSize"
          >
            <ComboboxOptions
              class="absolute mt-0.5 max-h-60 w-full min-w-[240px] overflow-auto rounded-md py-1 text-xs shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-30"
              @scroll="showMoreOnScroll"
            >
              <div
                v-if="filteredOptions.length === 0 && query !== ''"
                class="relative cursor-default select-none py-2 px-4 text-gray-700"
              >
                Nothing found.
              </div>
              <ComboboxOption
                v-for="option in visibleOptions"
                as="template"
                :key="option.value"
                :value="option.value"
                v-slot="{ selected, active }"
              >
                <li
                  class="relative cursor-default select-none py-2 pl-4 pr-2 lg:pl-6"
                  :class="{
                    'bg-amber-100 text-amber-900': active,
                    'text-gray-900': !active,
                  }"
                >
                  <span
                    class="block"
                    :class="{ 'font-medium': selected, 'font-normal': !selected }"
                  >
                    {{ option.label }}
                  </span>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center"
                    :class="{ 'text-amber-900': active, 'text-gray-900': !active }"
                  >
                    <CheckIcon class="pl-1 h-3 w-3 lg:h-5 lg:w-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </TransitionRoot>
        </div>
    </Combobox>
    </div>
    <!-- <base-popover-hover 
        v-if="isPopoverVisible"
        :xPosition="xPosition"
        :yPosition="yPosition"
      >
      <template v-slot:title>Selected Codes:</template>
      <template v-slot:body>
        <div v-for="value in displayOptions" :key="value">{{ value }}</div>
      </template>
    </base-popover-hover> -->
</template>

<script>
</script>
  
<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import {
Combobox,
ComboboxInput,
ComboboxButton,
ComboboxOptions,
ComboboxOption,
TransitionRoot,
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/vue/20/solid'

const props = defineProps({
    options: Array,
    modelValue: [String, Number, Array],
    singleValue: Boolean,
    inputClass: String,
    roundedClass: {
        type: String,
        default: 'rounded-b-md'
    },
    placeholder: String,
    // Single-value only: lets the user clear the selection by emptying the input
    nullable: Boolean,
    // Optional: render this many options at first and add more as the list is scrolled (default: all)
    pageSize: Number,
    // Optional: show an x that clears the selection
    clearable: Boolean
})

const xPosition = ref(0)
const yPosition = ref(0)
const inputElement = ref(null)
const isPopoverVisible = ref(false)

const emit = defineEmits(['update:modelValue'])

const showClear = computed(() => {
  const value = props.modelValue
  return props.clearable && value != null && value !== '' && !(Array.isArray(value) && value.length === 0)
})

let query = ref('')

function showPopover() {
  if(Array.isArray(props.modelValue) && props.modelValue.length > 1){
      isPopoverVisible.value = true;
      xPosition.value = inputElement.value.getBoundingClientRect().right
      yPosition.value = inputElement.value.getBoundingClientRect().top
  }
}

function hidePopover() {
  isPopoverVisible.value = false;
  xPosition.value = 0;
  yPosition.value = 0;
}

const display = () => {
  return props.options.filter(option => {
  if (Array.isArray(props.modelValue)) {
      // console.log('model value is array', props.modelValue)
      return props.modelValue.includes(option.value);
      }
      return props.modelValue === option.value;
  }).map(option => option.label).join(', ')
}

const displayOptions = computed(() => {
  return props.options.filter(option => {
  if (Array.isArray(props.modelValue)) {
      // console.log('model value is array', props.modelValue)
      return props.modelValue.includes(option.value);
      }
      return props.modelValue === option.value;
  }).map(option => option.label)
})

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (q === '') return props.options
  return props.options.filter((option) =>
    option.label.toLowerCase().includes(q)
  )
})

const visibleCount = ref(props.pageSize)

const visibleOptions = computed(() => {
  if (!props.pageSize) return filteredOptions.value
  return filteredOptions.value.slice(0, visibleCount.value)
})

// Near the bottom of the list, render the next batch of options
function showMoreOnScroll(event) {
  if (!props.pageSize) return
  const list = event.target
  if (list.scrollTop + list.clientHeight >= list.scrollHeight - 20) {
    visibleCount.value += props.pageSize
  }
}

// A new search starts again from the first batch
watch(query, () => {
  visibleCount.value = props.pageSize
})

</script>
  